<template>
  <div class="gg-controlbar" :class="`gg-controlbar--${stateClass}`">
    <button
      type="button"
      class="gg-controlbar__btn gg-controlbar__hardstop"
      :class="{ 'is-enabled': forceStopEnabled }"
      :disabled="!forceStopEnabled"
      @click="onClick('force-stop', forceStopEnabled)"
    >
      <v-icon size="20">mdi-close-octagon</v-icon>
      <span>Forçar parar</span>
    </button>

    <div class="gg-controlbar__main">
      <button
        type="button"
        class="gg-controlbar__nav"
        :class="{ 'is-enabled': navEnabled }"
        :disabled="!navEnabled"
        aria-label="Anterior"
        @click="onClick('previous', navEnabled)"
      >
        <v-icon size="24">mdi-skip-previous</v-icon>
      </button>

      <button
        type="button"
        class="gg-controlbar__btn gg-controlbar__take"
        :class="{ 'is-armed': takeArmed }"
        :disabled="!takeArmed"
        @click="onClick('take', takeArmed)"
      >
        <v-icon size="20">mdi-play</v-icon>
        <span>Take</span>
      </button>

      <button
        type="button"
        class="gg-controlbar__nav"
        :class="{ 'is-enabled': navEnabled }"
        :disabled="!navEnabled"
        aria-label="Próximo"
        @click="onClick('next', navEnabled)"
      >
        <v-icon size="24">mdi-skip-next</v-icon>
      </button>

      <button
        type="button"
        class="gg-controlbar__nav"
        :class="{ 'is-enabled': navEnabled }"
        :disabled="!navEnabled"
        aria-label="Continuar"
        @click="onClick('continue', navEnabled)"
      >
        <v-icon size="24">mdi-fast-forward</v-icon>
      </button>

      <button
        type="button"
        class="gg-controlbar__btn gg-controlbar__takeout"
        :class="{ 'is-enabled': takeoutEnabled }"
        :disabled="!takeoutEnabled"
        @click="onClick('takeout', takeoutEnabled)"
      >
        <v-icon size="20">mdi-stop</v-icon>
        <span>Takeout</span>
      </button>
    </div>

    <div class="gg-controlbar__chip-wrap">
      <span class="gg-controlbar__chip">
        <v-icon size="10">mdi-circle</v-icon>
        {{ chipLabel }}
      </span>
    </div>
  </div>
</template>

<script>
const STATES = ["OFF", "STBY", "PVW", "ON_AIR"];

export default {
  name: "GloboGraphicsControlBar",
  props: {
    // Transport state — the parent derives it from the playout flags. Drives the
    // status chip and the bar's colouring only; what the operator can actually do
    // is passed separately, because the chip answers "what is the playout doing"
    // while the buttons answer "is there something to do it to".
    state: {
      type: String,
      default: "OFF",
      validator: (v) => STATES.includes(v),
    },
    // There is a template the engine could roll to air right now.
    canTake: { type: Boolean, default: false },
    // There is an active playlist to step through.
    canNavigate: { type: Boolean, default: false },
  },
  computed: {
    stateClass() {
      return this.state.toLowerCase().replace("_", "-");
    },
    // Force-stop is only reachable while on air.
    forceStopEnabled() {
      return this.state === "ON_AIR";
    },
    navEnabled() {
      return this.canNavigate;
    },
    takeArmed() {
      return this.canTake;
    },
    // Takeout removes the on-air graphic — only meaningful when on air.
    takeoutEnabled() {
      return this.state === "ON_AIR";
    },
    chipLabel() {
      return this.state === "ON_AIR" ? "ON AIR" : this.state;
    },
  },
  methods: {
    onClick(event, enabled) {
      if (enabled) this.$emit(event);
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-controlbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
  padding: var(--sc-space-4);
  border-radius: var(--sc-radius-xl);
  background: var(--sc-gg-surface-1);
  font-family: "Inter", "Globotipo", sans-serif;

  .v-icon {
    color: inherit !important;
  }

  // A global `p, span, label` rule paints every label with the theme's secondary
  // text colour, which outranks plain inheritance — so each button's state colour
  // reached its icon but never its text, and Take/Forçar parar/Takeout all rendered
  // the same grey whatever state they were in. Labels follow the button, like icons.
  span {
    color: inherit;
  }
}

.gg-controlbar__main {
  display: flex;
  align-items: center;
  gap: var(--sc-space-4);
}

.gg-controlbar__chip-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 96px;
}

// ── Labelled buttons (Forçar parar / Take / Takeout) ─────────────────────
.gg-controlbar__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sc-space-1);
  min-height: 48px;
  min-width: 72px;
  padding: 0 var(--sc-space-3);
  border-radius: var(--sc-radius-md);
  font-family: inherit;
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  line-height: 20px;
  letter-spacing: 0.16px;
  white-space: nowrap;
  cursor: default;
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard);
}

.is-enabled,
.is-armed {
  cursor: pointer;

  &:hover {
    opacity: 0.88;
  }
}

// Forçar parar — bordered, red when live.
//
// Idle uses text-tertiary, not --sc-gg-disabled: at #4d4e51 the label sat only a
// shade above the #252527 bar and read as an artefact rather than a disabled
// control. The design distinguishes the two — disabled ICONS stay #4d4e51, disabled
// LABELS are Content/Neutral/Tertiary.
.gg-controlbar__hardstop {
  border: 1px solid var(--sc-gg-border-300);
  color: var(--sc-gg-text-tertiary);

  &.is-enabled {
    color: var(--sc-gg-critical);
  }
}

// Take — filled; light-grey idle, accent-blue armed. The FILL carries the state and
// the label stays black in both: on the light idle fill, disabled-grey text landed at
// ~3.8:1 and read as washed out rather than deliberately inactive.
.gg-controlbar__take {
  background: var(--sc-gg-neutral-fill);
  color: var(--sc-gg-accent-on);

  &.is-armed {
    background: var(--sc-gg-accent);
  }
}

// Takeout — bordered, accent when live. Same idle treatment as Forçar parar: the
// label is legible tertiary grey, and only the icon carries the darker disabled tone.
.gg-controlbar__takeout {
  border: 1px solid var(--sc-gg-border-300);
  color: var(--sc-gg-text-tertiary);

  .v-icon {
    color: var(--sc-gg-disabled) !important;
  }

  // Live, the label and icon go amber; the border stays accent, like the transport
  // buttons beside it. The border marks the control as available, the amber marks
  // what it does — recolouring both collapsed that into one signal.
  &.is-enabled {
    border-color: var(--sc-gg-accent);
    color: var(--sc-gg-warning);

    .v-icon {
      color: var(--sc-gg-warning) !important;
    }
  }
}

// ── Nav buttons (Previous / Next / Continuous) ───────────────────────────
.gg-controlbar__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--sc-radius-lg);
  background: var(--sc-gg-overlay-1);
  border: 1px solid var(--sc-gg-disabled);
  color: var(--sc-gg-disabled);
  cursor: default;
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard);

  &.is-enabled {
    background: transparent;
    border-color: var(--sc-gg-accent);
    color: var(--sc-gg-accent);
    cursor: pointer;

    &:hover {
      opacity: 0.88;
    }
  }
}

// ── Status chip ──────────────────────────────────────────────────────────
.gg-controlbar__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-2);
  min-height: 32px;
  min-width: 64px;
  padding: 0 var(--sc-space-2);
  border-radius: var(--sc-radius-pill);
  border: 1px solid var(--sc-gg-neutral-fill);
  background: var(--sc-gg-surface-1);
  color: var(--sc-gg-text-strong);
  font-family: inherit;
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  line-height: 20px;
  letter-spacing: 0.16px;
}

.gg-controlbar--off .gg-controlbar__chip {
  border-style: dashed;
  border-color: var(--sc-gg-disabled);
  color: var(--sc-gg-text-secondary);
}

.gg-controlbar--pvw .gg-controlbar__chip {
  border-color: var(--sc-gg-success-border);
  color: var(--sc-gg-success);
}

.gg-controlbar--on-air .gg-controlbar__chip {
  border-color: var(--sc-gg-critical);
  color: var(--sc-gg-critical);
}
</style>
